import Loader from '@/components/ui/loader';
import { useAuthStore } from '@/stores/auth-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** Paths accessible without authentication. */
const PUBLIC_PATHS = new Set<string>(['/sign-in']);

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const authState = useAuthStore((s) => s.authState);
  const init = useAuthStore((s) => s.init);
  const signOut = useAuthStore((s) => s.signOut);

  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const initState = useWorkspaceStore((s) => s.initState);
  const initWorkspaces = useWorkspaceStore((s) => s.init);
  const resetWorkspaces = useWorkspaceStore((s) => s.reset);

  // Boot Firebase auth listener once.
  useEffect(() => {
    init();
  }, []);

  // Trigger workspace bootstrap when signed in; reset on sign-out.
  useEffect(() => {
    if (authState === 'signed-in' && initState === 'idle') {
      void initWorkspaces();
    }
    if (authState === 'signed-out') {
      resetWorkspaces();
    }
  }, [authState, initState]);

  // If the user doesn't exist in our DB, sign them out immediately.
  useEffect(() => {
    if (initState === 'user-not-found') {
      void signOut();
    }
  }, [initState]);

  // Route based on auth + workspace state.
  useEffect(() => {
    if (!pathname) return;

    const isPublic = PUBLIC_PATHS.has(pathname);
    const isOnRoot = pathname === '/';
    const isOnCreateWorkspace = pathname === '/create-workspace';

    if (authState === 'signed-out' && !isPublic) {
      const returnUrl = encodeURIComponent(pathname);
      navigate(`/sign-in?returnUrl=${returnUrl}`, { replace: true });
      return;
    }

    if (authState === 'signed-in' && initState === 'loaded') {
      const firstWorkspace = workspaces[0];

      if (workspaces.length === 0 && !isOnCreateWorkspace) {
        navigate('/create-workspace', { replace: true });
        return;
      }

      if (workspaces.length > 0 && (isOnRoot || isPublic)) {
        navigate(`/workspace/${firstWorkspace.id}`, { replace: true });
        return;
      }
    }
  }, [authState, initState, pathname, workspaces]);

  // Show a loading screen while auth or workspace bootstrap is in progress.
  const isBootstrapping =
    authState === 'loading' ||
    (authState === 'signed-in' && (initState === 'idle' || initState === 'loading'));

  if (isBootstrapping && pathname && !PUBLIC_PATHS.has(pathname)) {
    return (
      <div className="bg-background flex min-h-screen flex-col items-center justify-center gap-4">
        <Loader className="mb-16" />
      </div>
    );
  }

  return children;
}
